import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaCheck, FaPlus, FaRedo, FaWindowClose } from 'react-icons/fa';
import PageContainer from '../../../../components/PageContainer';
import ListLayout from '../../../../components/ListLayout';
import Loading from '../../../../components/Loading';
import useAlertNotification from '../../../../context/AlertNotificationContext';
import ErrorRequestService from '../../../../services/errorRequest';
import FuncaoApiService from '../../../../services/api/FuncaoApiService';
import { storeFuncoes } from '../../../../store/actions/dadosActions';
import { columns } from './FuncaoListInfo';
import { syncFuncoes } from '../../../../services/api/SyncConvertConvenia';
import { SyncStatus, SyncStatusTipo } from '../../../../util/enum';
import CustomModal from '../../../../components/CustomModal';
import { Button } from 'react-bootstrap';
import useSyncStatus from '../../useSyncStatusConvenia';

const INITIAL_PAGEPARAMS = {
    orderBy: 'cod',
    orderDir: 'asc'
};
const ORDER_DIR_NUM = { asc: 1, desc: -1 };

function FuncaolList(props) {
    // CONTEXT
    const match = useRouteMatch();
    const history = useHistory();
    const alert = useAlertNotification();

    // REDUX
    const { funcoes } = useSelector(state => state.dados);

    const dispatch = useDispatch();
    const storeFuncaoData = useCallback(
        (dados) => dispatch(storeFuncoes(dados)),
        [ dispatch ]
    );

    // STATE
    const [rowsData, setRowsData] = useState(funcoes);
    const [filteredData, setFilteredData] = useState(funcoes);
    const [orderBy, setOrderBy] = useState(INITIAL_PAGEPARAMS.orderBy);
    const [orderDir, setOrderDir] = useState(INITIAL_PAGEPARAMS.orderDir);
    const [isLoading, setIsLoading] = useState(true);
    const [refresh, setRefresh] = useState(false);

    const { statusSincronizacao, statusMessage, startSyncCheck } = useSyncStatus( SyncStatusTipo.FUNCOES );

    // FUNCTIONS
    /** Define o clique sobre um registro da lista */
    const rowClick = (item) => {
        history.push(`${match.url}/${item.cod}`, item);
    }

    /** Adiciona click e atributos ao header */
    const getListHeader = () => {
        const headers = [];
        const defineOrder = (item) => {
            if (orderBy === item) {
                const newDir = (orderDir === 'asc') ? 'desc' : 'asc';
                setOrderDir(newDir);
                return;
            }
            setOrderBy(item);
        }
        columns.forEach(item => {
            const waySign = (ORDER_DIR_NUM[orderDir] > 0) ? '▴' : '▾';
            const orderInd = (orderBy === item.name) 
                ? {ordered: `${waySign}`}
                : {};
            const head = {
                ...item,
                ...orderInd,
                onClick: () => defineOrder(item.name),
            }
            headers.push(head);
        })
        return headers;
    }

    /** Adequa os dados ao formato para lista */
    const defineDataList = () => {
        const sortRows = (a, b) => {
            if (a[orderBy] < b[orderBy]) return (-1 * ORDER_DIR_NUM[orderDir]);
            if (a[orderBy] > b[orderBy]) return (1 * ORDER_DIR_NUM[orderDir]);
        }
        const dataList = [];
        const rows = filteredData || [];
        rows.sort(sortRows)
            .forEach(item => {
                const row = {
                    onClick: () => rowClick(item),
                    data: columns.map(col => ({
                        value: item[col.name],
                        className: col.className
                    }))
                };
                dataList.push(row);
            });
        return dataList;
    }

    /* Filtra a lista de dados */
    const filterList = (params) => {
        const { column, filter } = params;

        if (!column || !filter) {
            setFilteredData(rowsData);
            return
        }
        
        const filtered = rowsData.filter(item => {
            const lwData = String(item[column]).toLowerCase();
            const lwFilter = String(filter).toLowerCase();

            return lwData.includes(lwFilter);
        });
        setFilteredData(filtered);
    }

    /** Traz as funcoes cadastradas no database  */
    const getDataList = useCallback(async () => {
        try {
            // @TODO: Rever situação de uso do REDUX para devida atualização das listas
            // if (!funcoes) {
                setIsLoading(true);
                const resp = await FuncaoApiService.getAll().then(r => r.data.data);
                storeFuncaoData(resp);
            // }
        }
        catch (err) {
            const errorService = new ErrorRequestService(err);
            const errorMsg = errorService.getErrors(); 
            console.log(errorMsg);
            alert.error(errorMsg);
        }
        finally {
            setIsLoading(false);
        }
    }, [funcoes, storeFuncaoData]);

    const handleUpdateFuncoesConvenia = () => {
        CustomModal.show({
            body: 'Ao iniciar essa ação, ela não poderá ser interrompida. Tem certeza que deseja continuar?',
            footer: (
                <>
                    <Button 
                        variant="outline-success" 
                        onClick={async () => {
                            try {
                                await syncFuncoes();
                                startSyncCheck();
                                setRefresh(!refresh);
                            } catch (error) {
                                alert.error("Erro " + error.status + " ao iniciar a sincronização das funções. Nenhuma alteração foi feita.");
                                console.error("Erro ao sincronizar as funções.", error);
                            } finally {
                                CustomModal.hide();
                            }
                        }}
                    >
                        Sim
                    </Button>
                    <Button variant="outline-danger" onClick={() => CustomModal.hide()}>Cancelar</Button>
                </>
            )
        })
    }

    // EFFECTS
    useEffect(getDataList, []);

    useEffect(() => {
        setRowsData(funcoes);
        setFilteredData(funcoes);
    }, [funcoes])

    const pageActions = useMemo(() => [
        {
            label: statusMessage.message,
            icon: statusMessage.icon,
            color: 'outline',
            type: 'text',
            disabled: true
        },
        { 
            label: 'Atualizar',
            icon: <FaRedo />,
            color: 'primary',
            type: 'button',
            tooltipText: 'Sincroniza todas as funções do Convenia com o Convert',
            tooltipKey: 'funcoes',
            onClick: () => { handleUpdateFuncoesConvenia() },
            disabled: statusSincronizacao === SyncStatus.IN_PROGRESS
        }
    ], [statusMessage]);

    useEffect(() => {
        if (statusSincronizacao === SyncStatus.COMPLETED && refresh) {
            setTimeout(() => window.location.reload(), 300);
            setRefresh(false);
        }
    }, [statusSincronizacao]);

    const title = 'Lista de Funções';

    return (
        <PageContainer 
            title={title}
            actions={pageActions}
        >
            { isLoading 
                ? <Loading message="Carregando..." />
                : <><ListLayout
                    columns={getListHeader()}
                    rows={defineDataList()}
                    onSubmitFilter={filterList}  
                /><CustomModal /></>
                
            }

        </PageContainer>
    )
}

export default FuncaolList;
